/**
 * @file 計画に出てくる本が本だなにあるかを、ISBN で突き合わせる。
 *
 * @what: 本だなの資料から ISBN の対応表を作り、「持っているか」と「その本を読む面への道」を返す。
 *   別冊 (companion_of を持つ資料) には ISBN を入れない運用なので、本体だけが当たる。
 * @why: 書名では突き合わせられない (本だなは自炊時の正式名称、ルートや教材は出典の表記で語順まで違う)。
 *   計画の面はいくつもの場所で本の表紙を出すので、持っているかどうかの判定を1か所に置き、
 *   どの場所でも同じ印が出るようにする (代表の要望、2026-09-08)。
 * @failure: 副作用を持たない。本だながまだ読めていないときは NO_SHELF_BOOKS を使い、印を出さない
 *   (「持っていない」と断定はせず、印が出ないだけである)。
 */

import type { StudySource } from "../api/sources";

export interface ShelfBooks {
  /** 本だなにこの ISBN の本があるかどうかを返す。 */
  has(isbn: string | undefined): boolean;
  /** 本だなのその本を読む面への道を返す。無ければ undefined を返す。 */
  href(isbn: string | undefined): string | undefined;
}

/** 本だなを渡していない・まだ読めていないときに使う (どの本にも印を出さない)。 */
export const NO_SHELF_BOOKS: ShelfBooks = {
  has: () => false,
  href: () => undefined,
};

export function shelfBooks(
  familyId: string,
  sources: readonly StudySource[] | null | undefined,
): ShelfBooks {
  if (!sources) return NO_SHELF_BOOKS;
  const byIsbn = new Map<string, string>();
  for (const s of sources) if (s.isbn && !byIsbn.has(s.isbn)) byIsbn.set(s.isbn, s.id);
  const sourceId = (isbn: string | undefined) =>
    isbn === undefined ? undefined : byIsbn.get(isbn);
  return {
    has: (isbn) => sourceId(isbn) !== undefined,
    href: (isbn) => {
      const id = sourceId(isbn);
      return id === undefined
        ? undefined
        : `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(id)}`;
    },
  };
}
